import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { BackButton } from '../components/common/BackButton';
import { CheckCircle, Phone, Mail, Globe, Building2 } from 'lucide-react';

export function BookAuditPage() {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    website: '',
    interests: [] as string[],
    description: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (interest: string) => {
    const newInterests = formData.interests.includes(interest)
      ? formData.interests.filter(i => i !== interest)
      : [...formData.interests, interest];
    setFormData({ ...formData, interests: newInterests });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // TODO: Connect to backend / Zapier / Google Sheets
    // Form submission will be wired up later
    console.log('Form data:', formData);
    alert('Thank you! Form submission will be connected soon.');
  };

  const interestOptions = [
    'Missed Call & Lead Rescue System',
    'Chatbots & Phone Agents',
    'Websites & Landing Pages',
    'General Automation Audit / Not Sure Yet',
  ];

  return (
    <>
      <Helmet>
        <title>Book Your Free Lead Rescue Audit | Priority AI Services</title>
        <meta
          name="description"
          content="Book a free 15-minute Lead Rescue Audit. We'll review how calls and inquiries are handled in your business and map out where automation could help."
        />
      </Helmet>

      <div className="min-h-screen pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <BackButton />

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Book Your Free 15-Minute Lead Rescue Audit
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We'll quickly review how calls and inquiries are handled in your business
              and map out where a simple automation system could plug the leaks.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Left Column - Form */}
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-blue-500/20">
              <h2 className="text-2xl font-bold text-white mb-6">Tell Me About Your Business</h2>
              <p className="text-gray-300 mb-6">
                Fill in the details below, and I'll review it personally and reply with next steps
                or a link to book a quick call.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="businessName" className="block text-sm font-medium text-gray-300 mb-2">
                    Business Name *
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      required
                      value={formData.businessName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="Your Business Name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="john@business.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone (Optional)
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-300 mb-2">
                    Website URL *
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3.5 w-5 h-5 text-gray-500" />
                    <input
                      type="url"
                      id="website"
                      name="website"
                      required
                      value={formData.website}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="https://yourbusiness.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    What are you interested in? *
                  </label>
                  <div className="space-y-3">
                    {interestOptions.map((option) => (
                      <label key={option} className="flex items-start gap-3 cursor-pointer group">
                        <div className="relative flex items-center justify-center mt-0.5">
                          <input
                            type="checkbox"
                            checked={formData.interests.includes(option)}
                            onChange={() => handleCheckboxChange(option)}
                            className="w-5 h-5 bg-gray-800/50 border border-gray-700 rounded cursor-pointer checked:bg-blue-500 checked:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                          />
                          {formData.interests.includes(option) && (
                            <CheckCircle className="absolute w-5 h-5 text-white pointer-events-none" />
                          )}
                        </div>
                        <span className="text-gray-300 group-hover:text-white transition-colors">
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                    Briefly describe your business and main service *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                    placeholder="E.g., We're a dental clinic with 3 locations. We get about 50-100 calls per day and struggle to follow up with missed calls..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold rounded-md hover:from-green-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  Submit Inquiry
                </button>

                <p className="text-sm text-gray-400 text-center">
                  Prefer to book directly? Scroll down to book a call on the calendar below.
                </p>
              </form>
            </div>

            {/* Right Column - Calendar Placeholder */}
            <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-lg border border-blue-500/20">
              <h2 className="text-2xl font-bold text-white mb-6">Or Book a Call Directly</h2>
              <p className="text-gray-300 mb-6">
                Choose a time that works for you, and we'll have a quick 15-minute conversation
                about your business.
              </p>

              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-8 min-h-[500px] flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <Phone className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-gray-400 mb-2">Calendar Widget Coming Soon</p>
                  <p className="text-sm text-gray-500">
                    {/* TODO: Embed Calendly widget here once calendar link is ready */}
                    Calendly integration will be added here
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-3 text-sm text-gray-400">
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>No obligation, no pressure - just a friendly conversation</span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>15 minutes or less</span>
                </p>
                <p className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>Walk away with clear next steps</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
